import { CaretDown, CaretUp } from '@assets/svgs';
import { Divider, Grid, SxProps, Typography } from '@mui/material';
import { Button } from '@components/button';
import { CustomerDetailsRow } from '@pages/CustomerDetails/CustomerDetails.constants';
import { DetailRow } from '@components/detailsPanel/DetailRow.type';
import { PolicyDetailsRow } from '@pages/PolicyDetails/PolicyDetails.constants';
import { Fragment, ReactNode, useState } from 'react';
import { PolicyStatusText } from '@models/policy';
import { IndicationDetailsRow } from '@pages/IndicationDetails/IndicationDetails.constants';

export type BaseField = DetailRow & {
  field: string;
  name: string;
  valueGetter: (data: any) => string | undefined | null | ReactNode;
  getSxProps?: (data: any) => SxProps;
  hidden?: (data: any) => boolean;
};

export type DetailPanelSection = {
  title?: string;
  shownInitially?: boolean;
  // FUTURE TODO: update to use generics
  rows: { fields: CustomerDetailsRow[] | PolicyDetailsRow[] | IndicationDetailsRow[] | BaseField[] }[];
};

type DetailsPanelProps = {
  sections: DetailPanelSection[];
  dataTestId: string;
  collapsible?: boolean;
  spacing?: string | number | { xs: number; sm: number };
};

type WrapperProps = {
  children: ReactNode;
  item: CustomerDetailsRow | PolicyDetailsRow | IndicationDetailsRow | BaseField;
  sx?: SxProps;
};

const Wrapper = ({ children, item, sx }: WrapperProps) => {
  return (
    <Grid
      size={{ xs: 6, sm: 6, md: 3, lg: 2 }}
      sx={{
        marginBottom: '12px',
        flexDirection: item.multiline ? 'column' : 'row',
        alignItems: 'flex-start',
        ...sx,
        display: 'flex',
        justifyContent: 'left',
      }}
    >
      {children}
    </Grid>
  );
};

export const DetailsPanel = ({ spacing, sections, dataTestId, collapsible = true }: DetailsPanelProps) => {
  const [showDetails, setShowDetails] = useState<boolean>(!collapsible);

  const handleShowDetails = () => {
    setShowDetails((prev) => !prev);
  };

  return (
    <Grid
      container
      spacing={spacing ?? { xs: 0, sm: 2 }}
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        width: '100%',
      }}
    >
      {sections
        ?.filter((d) => (showDetails ? true : d.shownInitially))
        ?.map(({ rows, title }, index) => {
          const key = `section-${index}-${title}`;
          return (
            <Fragment key={key}>
              {title && (
                <Grid
                  key={key}
                  size={{ xs: 12 }}
                  sx={{
                    mb: 3,
                    mt: 1,
                  }}
                >
                  <Typography variant='h6'>{title}</Typography>
                </Grid>
              )}
              {rows.map((row, index) => {
                const key = `row-${index}`;

                return (
                  <Grid key={key} container size={{ xs: 12 }} spacing={0}>
                    {row.fields.map((el) => (
                      <Fragment key={`${el.id}-field`}>
                        <Wrapper item={el}>
                          <Typography
                            variant='bodySmall'
                            sx={{
                              width: '100%',
                              marginRight: 1,
                              marginBottom: el.multiline ? 1 : undefined,
                              color: 'text.subdued',
                            }}
                          >
                            {`${el.field}:`}
                          </Typography>
                        </Wrapper>
                        <Wrapper key={el.id} item={el} sx={{}}>
                          <Typography
                            data-testid={el.field?.toLowerCase()}
                            variant='bodySmall'
                            sx={{
                              wordBreak: 'break-word',
                              width: '100%',
                              fontWeight: el.noBold ? 'default' : 'bold',
                              pr: 2,
                              ...el.sxProps,
                            }}
                          >
                            {PolicyStatusText[el.value as keyof typeof PolicyStatusText] || el.value}
                          </Typography>
                        </Wrapper>
                        {el.width === '100%' && <Grid size={{ sm: 0, xs: 0, md: 4, lg: 8 }} />}
                      </Fragment>
                    ))}
                  </Grid>
                );
              })}
            </Fragment>
          );
        })}
      {collapsible ? (
        <Grid
          sx={{
            width: '100%',
            mt: 2,
            pb: 0,
            mb: 0,
          }}
        >
          <Divider sx={{ ml: 2 }} />

          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              variant='text'
              sx={{ margin: '10px 0px', mb: 0, pb: 0 }}
              endIcon={showDetails ? <CaretUp /> : <CaretDown />}
              onClick={handleShowDetails}
            >
              {showDetails ? 'Collapse' : 'Expand'} Details
            </Button>
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  );
};
